import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { APIStatus } from '../../@types/APIStatus';
import { SortBy } from '../../@types/filters';
import useBundle from '../../hooks/useBundle';
import { TableStyleClasses } from '../../theme/table/TableClasses';
import { CardError } from '../general/CardError';
import { CardLoading } from '../general/CardLoading';
import { CardNoResult } from '../general/CardNoResult';
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import SummaryHeaderCell from '../tables/SummaryHeaderCell';

type Props = {
  rowsPerPage: number;
  sortBy: SortBy;
  onSortChange: (sortBy: SortBy) => void;
};

function BundleTable({ rowsPerPage, sortBy, onSortChange }: Props) {
  const { t: translate } = useTranslation(['component']);
  const { currentLang } = useLocales();
  const { user } = useAuth();
  const classes = TableStyleClasses({
    height: 75,
    numRows: rowsPerPage
  });

  const {
    summary,
    response: { rows },
    APIStatus: state,
    error
  } = useBundle();
  const iso = user.currency && user.currency.iso ? user.currency.iso : 'EUR';
  const euroFormat = new Intl.NumberFormat(currentLang.value, {
    style: 'currency',
    currency: iso,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <Paper variant="outlined" className={classes.paper}>
      {state === APIStatus.PENDING && <CardLoading />}
      {error && <CardError />}
      {state === APIStatus.FULFILLED && rows.length === 0 && !error && (
        <CardNoResult />
      )}
      <TableContainer className={`${classes.tableContainer} smallHeader`}>
        <Table stickyHeader>
          <TableHead sx={{ fontSize: '14px' }}>
            <TableRow>
              {summary.map((header, index) => (
                <SummaryHeaderCell
                  key={`${index}_${header.field}`}
                  index={index}
                  field={header.field}
                  onSortChange={onSortChange}
                  sticky={false}
                  canSort={header.canSort}
                  active={sortBy.by == header.field}
                  header={{
                    align: header.align,
                    label: translate(header.label)
                  }}
                  direction={sortBy.order}
                  summary={header.summary}
                  minWidth={header.minWidth}
                  infoStyle={header.infoStyle}
                  smallHeight={true}
                  // showSummary={true}
                  showTotal={false}
                  // colSpan={index === 0 ? 2 : 0}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(
              (
                {
                  articleId,
                  picture,
                  variationId,
                  name,
                  asin,
                  ean,
                  sku,
                  qty,
                  purchasePrice,
                  total
                },
                i
              ) => (
                <TableRow
                  hover
                  key={`${i}_${variationId}`}
                  className={classes.tableRow}
                >
                  <TableCell>
                    <Typography
                      component={RouterLink}
                      to={`/product/${articleId}/0`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {variationId}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      textAlign="start"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{asin}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{ean}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{sku}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{qty}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {purchasePrice ? euroFormat.format(purchasePrice) : '-'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {total ? euroFormat.format(total) : '-'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default BundleTable;
